import { useEffect, useRef } from "react";
import jwt_decode from "jwt-decode";

import LOCAL_STORAGE from "@/constants/local-storage";
import { TOKEN_REFRESH_THRESHOLD_MS } from "@/constants/auth-session.constants";
import { IDecodedToken } from "@/interfaces/auth.interfaces";
import { refreshSession } from "@/auth/token-refresh-manager";
import { logSessionEvent } from "@/utils/session-log";

const CHECK_INTERVAL_MS = 30_000;


/**
 * Renovação proativa do access token.
 *
 * Sem isso o token só é renovado quando uma chamada de API falha, então uma
 * sessão parada (ou em telas sem requisição) expira e o usuário é deslogado
 * mesmo estando ativo.
 */
export function useProactiveTokenRefresh(enabled: boolean) {
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;

    const tick = async () => {
      if (isRefreshingRef.current) return;

      const accessToken = localStorage.getItem(LOCAL_STORAGE.accessTokenKey);
      const refreshToken = localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);

      if (!accessToken || !refreshToken) return;

      let decoded: IDecodedToken;
      try {
        decoded = jwt_decode(accessToken);
      } catch {
        return;
      }

      const remainingMs = decoded.exp * 1000 - Date.now();
      if (remainingMs > TOKEN_REFRESH_THRESHOLD_MS) return;

      isRefreshingRef.current = true;
      try {
        const outcome = await refreshSession();
        logSessionEvent(
          outcome.status === "success"
            ? "refresh proativo OK"
            : `refresh proativo FALHOU: ${outcome.status} (sessão mantida)`,
          { remainingMinutes: Math.round(remainingMs / 60000) }
        );
      } finally {
        isRefreshingRef.current = false;
      }

    };

    tick();
    const intervalId = window.setInterval(tick, CHECK_INTERVAL_MS);

    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled]);
}

export default useProactiveTokenRefresh;
